import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight } from "lucide-react";

export default function Register() {
    const { user, register } = useAuth();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [terms, setTerms] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user) return <Navigate to="/dashboard" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!terms) {
            setErr("You must accept the Flexible Office Pod Licence Agreement to create an account.");
            return;
        }
        setLoading(true);
        try {
            await register(name, email, password, terms);
            navigate("/dashboard");
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
            <div className="flex flex-col p-8 sm:p-12 lg:p-16">
                <Link to="/" className="flex items-center gap-2.5 mb-12 w-fit" data-testid="register-logo-link">
                    <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">A</div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                    </div>
                </Link>
                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Create account</p>
                    <h1 className="font-heading text-4xl tracking-tight font-bold text-slate-900">Start booking the pod!</h1>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">It takes about 30 seconds — no credit card required to sign up.</p>
                    <form onSubmit={submit} className="mt-10 space-y-5">
                        <div>
                            <Label htmlFor="name" className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Full name</Label>
                            <Input
                                id="name"
                                required
                                data-testid="register-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                placeholder="Jane Doe"
                            />
                        </div>
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                data-testid="register-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                minLength={6}
                                data-testid="register-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                placeholder="At least 6 characters"
                            />
                        </div>
                        {err && (
                            <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                                {err}
                            </div>
                        )}
                        <div className="flex items-start gap-3 pt-1">
                            <Checkbox
                                id="register-terms"
                                checked={terms}
                                onCheckedChange={(v) => setTerms(!!v)}
                                data-testid="register-terms-checkbox"
                                className="mt-0.5"
                            />
                            <label htmlFor="register-terms" className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none">
                                I have read and agree to the{" "}
                                <Link
                                    to="/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="register-terms-link"
                                    className="text-brand font-medium hover:underline underline-offset-4"
                                >
                                    Flexible Office Pod Licence Agreement
                                </Link>.
                            </label>
                        </div>
                        <Button
                            type="submit"
                            data-testid="register-submit-btn"
                            disabled={loading || !terms}
                            className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-md group"
                        >
                            {loading ? "Creating account…" : (
                                <>
                                    Create account
                                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                                </>
                            )}
                        </Button>
                    </form>
                    <p className="mt-8 text-sm text-slate-600">
                        Already have an account?{" "}
                        <Link to="/login" data-testid="register-to-login-link" className="text-brand font-medium hover:underline underline-offset-4">
                            Sign in →
                        </Link>
                    </p>
                    <div className="mt-6 pt-6 border-t border-slate-200">
                        <Link to="/admin-login" data-testid="register-to-admin-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-mono text-slate-500 hover:text-slate-900 transition-colors group">
                            <span className="h-1.5 w-1.5 bg-amber-500 rounded-full" />
                            Admin sign-in
                            <span className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">→</span>
                        </Link>
                    </div>
                </div>
            </div>
            <div className="hidden lg:block relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1760611656615-db3fad24a314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBwb2R8ZW58MHx8fHwxNzc4MDExOTUwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Modern office space"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12 text-white">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/80 mb-3">A consulting space</p>
                    <h2 className="font-heading text-3xl tracking-tight font-bold leading-tight max-w-md">Reserve a pod in seconds. Pay only for what you use.</h2>
                </div>
            </div>
        </div>
    );
}
